/**
 * MarkdownText
 * ----
 * Our shared markdown renderer. Turns a markdown string (e.g. an AI-generated
 * resource digest, tutor message, or feedback) into styled native components,
 * themed with the Flare design tokens. Built on `react-native-markdown-display`
 * so it works on web and native without a WebView.
 */
import React from 'react';
import { Linking, TextStyle } from 'react-native';
import Markdown, { MarkdownProps } from 'react-native-markdown-display';
import { useToken, Box } from '@gluestack-ui/themed';

interface MarkdownTextProps {
  content?: string;
  /** Base body font size in px (headings/code scale from this). Defaults to 13. */
  fontSize?: number;
  testID?: string;
}

const MarkdownText: React.FC<MarkdownTextProps> = ({ content = '', fontSize = 13, testID }) => {
  const fontFamily = useToken('fonts', 'body') as string;
  // @ts-expect-error - textBody is a semantic token
  const textBody = (useToken('colors', 'textBody') as string) || '#374151';
  // @ts-expect-error - textHeading is a semantic token
  const textHeading = (useToken('colors', 'textHeading') as string) || '#111827';
  // @ts-expect-error - textBrand is a semantic token
  const textBrand = (useToken('colors', 'textBrand') as string) || '#2563eb';
  // @ts-expect-error - borderLight is a semantic token
  const borderLight = (useToken('colors', 'borderLight') as string) || '#e5e7eb';
  // @ts-expect-error - bgSubtle is a semantic token
  const bgSubtle = (useToken('colors', 'bgSubtle') as string) || '#f9fafb';

  // Keys match react-native-markdown-display style API; eslint cannot see cross-library usage.
  /* eslint-disable react-native/no-unused-styles */
  const styles = React.useMemo(
    (): MarkdownProps['style'] => ({
      body: {
        fontFamily,
        fontSize,
        lineHeight: Math.round(fontSize * 1.5),
        color: textBody,
      },
      heading1: {
        fontFamily, fontSize: fontSize + 5, fontWeight: '700', color: textHeading, marginTop: 8, marginBottom: 4,
      },
      heading2: {
        fontFamily, fontSize: fontSize + 3, fontWeight: '700', color: textHeading, marginTop: 8, marginBottom: 4,
      },
      heading3: {
        fontFamily, fontSize: fontSize + 1, fontWeight: '600', color: textHeading, marginTop: 6, marginBottom: 2,
      },
      heading4: {
        fontFamily, fontSize, fontWeight: '600', color: textHeading, marginTop: 4, marginBottom: 2,
      },
      paragraph: { marginTop: 0, marginBottom: 8 },
      strong: { fontWeight: '700' as TextStyle['fontWeight'], color: textHeading },
      em: { fontStyle: 'italic' },
      bullet_list: { marginBottom: 8 },
      ordered_list: { marginBottom: 8 },
      list_item: { marginBottom: 2 },
      code_inline: {
        fontSize: fontSize - 1,
        color: textHeading,
        backgroundColor: bgSubtle,
        borderRadius: 4,
        paddingHorizontal: 4,
        paddingVertical: 1,
      },
      code_block: {
        fontSize: fontSize - 1,
        color: textBody,
        backgroundColor: bgSubtle,
        borderColor: borderLight,
        borderWidth: 1,
        borderRadius: 6,
        padding: 8,
      },
      fence: {
        fontSize: fontSize - 1,
        color: textBody,
        backgroundColor: bgSubtle,
        borderColor: borderLight,
        borderWidth: 1,
        borderRadius: 6,
        padding: 8,
      },
      link: { color: textBrand, textDecorationLine: 'underline' },
      blockquote: {
        backgroundColor: bgSubtle,
        borderColor: borderLight,
        borderLeftWidth: 3,
        paddingHorizontal: 8,
        paddingVertical: 4,
        marginBottom: 8,
      },
      hr: { backgroundColor: borderLight, height: 1, marginVertical: 8 },
      table: { borderColor: borderLight, borderWidth: 1, borderRadius: 6 },
      th: { padding: 6, color: textHeading },
      td: { padding: 6, borderColor: borderLight },
      tr: { borderColor: borderLight },
      blocklink: { borderColor: textBrand },
    }),
    [fontFamily, fontSize, textBody, textHeading, textBrand, borderLight, bgSubtle],
  );
  /* eslint-enable react-native/no-unused-styles */

  const onLinkPress = (url: string) => {
    if (url) {
      Linking.openURL(url).catch(() => {});
    }
    return false;
  };

  return (
    <Box testID={testID}>
      <Markdown style={styles} onLinkPress={onLinkPress}>
        {content}
      </Markdown>
    </Box>
  );
};

export default React.memo(MarkdownText);
